import { useEffect, useState } from "react";
import { useGame } from "@/game/GameContext";
import { CHARACTERS } from "@/game/gameData";
import PixelButton from "@/components/PixelButton";
import { sfx, startMenuMusic, isMusicEnabled } from "@/game/audioEngine";

export default function CharacterSelectScreen() {
  const { state, dispatch } = useGame();
  const [hoverIdx, setHoverIdx] = useState(0);

  useEffect(() => {
    if (isMusicEnabled()) startMenuMusic();
  }, []);

  const selected = state.character;
  const active = CHARACTERS[hoverIdx];

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="pixel-font text-xl md:text-2xl text-[#F26522]" data-testid="character-select-title">
            CHOOSE YOUR TRAILBLAZER
          </h2>
          <p className="vt323 text-xl text-white/80 mt-2">
            Each character has unique strengths and one special ability per game.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {CHARACTERS.map((c, idx) => {
              const isSelected = selected && selected.id === c.id;
              return (
                <button
                  type="button"
                  key={c.id}
                  data-testid={`character-card-${c.id}`}
                  onMouseEnter={() => setHoverIdx(idx)}
                  onFocus={() => setHoverIdx(idx)}
                  onClick={() => {
                    sfx.select();
                    dispatch({ type: "PICK_CHARACTER", character: c });
                  }}
                  className={`text-left bg-[#1E293B] border-4 ${
                    isSelected ? "border-[#F26522]" : "border-black"
                  } shadow-[4px_4px_0_0_#000] hover:shadow-[2px_2px_0_0_#000] hover:translate-x-[2px] hover:translate-y-[2px] transition-all p-3`}
                >
                  <div className="aspect-square bg-[#0F172A] border-4 border-black mb-2 overflow-hidden relative">
                    <img
                      src={c.avatar}
                      alt={c.name}
                      className="w-full h-full object-cover pixelated"
                    />
                    {/* Subtle inner pixel frame for set consistency */}
                    <div className="pointer-events-none absolute inset-0 border-2 border-[#F58634]/30" />
                  </div>
                  <div className="pixel-font text-[10px] text-[#F26522] mb-1">{c.name.toUpperCase()}</div>
                  <div className="text-xs text-white/70 leading-snug">{c.ability.name}</div>
                </button>
              );
            })}
          </div>

          {/* Details */}
          <div className="pixel-panel space-y-3" data-testid="character-detail">
            <div className="flex items-center gap-3">
              <img
                src={active.avatar}
                alt={active.name}
                className="w-16 h-16 border-4 border-black pixelated object-cover"
              />
              <div>
                <div className="pixel-font text-sm text-[#F26522]">{active.name}</div>
                <div className="text-xs italic mt-1">"{active.quote}"</div>
              </div>
            </div>
            <div>
              <div className="pixel-font text-[10px] text-[#1A1A1A]/70 mb-1">STRENGTHS</div>
              <div className="flex flex-wrap gap-1">
                {active.strengths.map((s) => (
                  <span key={s} className="bg-emerald-100 border-2 border-black px-2 py-0.5 text-xs">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="pixel-font text-[10px] text-[#1A1A1A]/70 mb-1">WEAKNESS</div>
              <span className="bg-red-100 border-2 border-black px-2 py-0.5 text-xs">{active.weakness}</span>
            </div>
            <div>
              <div className="pixel-font text-[10px] text-[#1A1A1A]/70 mb-1">SPECIAL ABILITY</div>
              <div className="bg-orange-100 border-2 border-black p-2 text-sm">
                <b>{active.ability.name}</b> — {active.ability.description}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <PixelButton variant="secondary" onClick={() => dispatch({ type: "GOTO", screen: "start" })}>
            ← Back
          </PixelButton>
          <PixelButton
            data-testid="character-continue"
            disabled={!selected}
            onClick={() => dispatch({ type: "GOTO", screen: "business" })}
          >
            Continue ▶
          </PixelButton>
        </div>
        {selected && (
          <p className="text-center vt323 text-xl text-emerald-300 mt-3">
            Selected: {selected.name}
          </p>
        )}
      </div>
    </div>
  );
}
